import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface OrbitSandboxProps {
  width?: number;
  height?: number;
}

export const OrbitSandbox: React.FC<OrbitSandboxProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const [velocityMultiplier, setVelocityMultiplier] = useState(1.0);
  const [status, setStatus] = useState<'stable' | 'slow' | 'fast'>('stable');

  // We use refs so the draw loop has continuous access to physics state without re-binding.
  const stateRef = useRef({
    x: size / 2 + 100,
    y: size / 2,
    vx: 0,
    vy: 1.5, // Base stable velocity
    multiplier: 1.0,
    crashed: false,
    escaped: false,
    resetTimer: 0,
  });

  // Keep ref in sync with state for the UI slider
  useEffect(() => {
    stateRef.current.multiplier = velocityMultiplier;
    if (velocityMultiplier < 0.6) setStatus('slow');
    else if (velocityMultiplier > 1.4) setStatus('fast');
    else setStatus('stable');
  }, [velocityMultiplier]);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;
    const G_M = 225; // Gravitational parameter (mass of sun * G)

    const st = stateRef.current;

    // Physics Update
    if (!st.crashed && !st.escaped) {
      // Calculate distance
      const dx = st.x - centerX;
      const dy = st.y - centerY;
      const distSq = dx * dx + dy * dy;
      const dist = Math.sqrt(distSq);

      // Simple gravity: F = G*M / r^2
      // Acceleration vector
      const a = G_M / distSq;
      const ax = -a * (dx / dist);
      const ay = -a * (dy / dist);

      // Apply velocity multiplier ONLY to current velocity to see immediate effect
      // Actually, standardizing the multiplier application:
      // If we continuously apply multiplier, it accelerates unnaturally. 
      // We'll apply it dynamically to the base stable orbit velocity or just scale the velocity magnitude.
      // A better sandbox feel: The multiplier directly scales the current velocity magnitude towards the target.
      
      const currentSpeed = Math.sqrt(st.vx * st.vx + st.vy * st.vy);
      // We want the base speed for this orbit to be ~1.5. 
      // We'll gently lerp the speed to baseSpeed * multiplier
      const targetSpeed = 1.5 * st.multiplier;
      const speedRatio = targetSpeed / (currentSpeed || 1);
      
      // Gradually adjust velocity to match the requested multiplier
      st.vx += (st.vx * speedRatio - st.vx) * 0.05;
      st.vy += (st.vy * speedRatio - st.vy) * 0.05;

      // Update velocity with gravity
      st.vx += ax;
      st.vy += ay;

      // Update position
      st.x += st.vx;
      st.y += st.vy;

      // Check conditions
      if (dist < 15) {
        st.crashed = true;
        st.resetTimer = 120; // 2 seconds at 60fps
      } else if (dist > size) {
        st.escaped = true;
        st.resetTimer = 120;
      }
    } else {
      // Handle reset
      st.resetTimer--;
      if (st.resetTimer <= 0) {
        // Reset to initial stable orbit
        st.x = centerX + 100;
        st.y = centerY;
        st.vx = 0;
        st.vy = 1.5 * st.multiplier;
        st.crashed = false;
        st.escaped = false;
      }
    }

    // Draw Sun
    ctx.save();
    const sunGrad = ctx.createRadialGradient(centerX, centerY, 5, centerX, centerY, 25);
    sunGrad.addColorStop(0, '#FFFFFF');
    sunGrad.addColorStop(0.5, '#FDE047');
    sunGrad.addColorStop(1, 'rgba(253, 224, 71, 0)');
    ctx.fillStyle = sunGrad;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 25, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Draw Crash Flash
    if (st.crashed && st.resetTimer > 100) {
      ctx.fillStyle = `rgba(255, 0, 0, ${(st.resetTimer - 100) / 20})`;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 40, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw Planet
    if (!st.crashed) {
      ctx.fillStyle = '#4A90D9';
      ctx.beginPath();
      ctx.arc(st.x, st.y, 6, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw velocity vector (tail)
      ctx.strokeStyle = 'rgba(74, 144, 217, 0.5)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(st.x, st.y);
      ctx.lineTo(st.x - st.vx * 10, st.y - st.vy * 10);
      ctx.stroke();
    }

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size, position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #1E293B' }}>
        <canvas ref={canvasRef} />
      </div>
      
      <div style={{ marginTop: '20px', width: '100%', maxWidth: '300px', textAlign: 'center' }}>
        <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px', color: '#94A3B8' }}>
          Planet Speed: {(velocityMultiplier).toFixed(1)}x
        </label>
        <input 
          type="range" 
          min="0" 
          max="200" 
          value={velocityMultiplier * 100} 
          onChange={(e) => setVelocityMultiplier(Number(e.target.value) / 100)}
          style={{ width: '100%', cursor: 'pointer' }}
        />
        <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 'bold', height: '20px', color: status === 'stable' ? '#4ADE80' : status === 'slow' ? '#F87171' : '#60A5FA' }}>
          {status === 'stable' && 'Stable Orbit'}
          {status === 'slow' && 'Orbit too slow → Falling into Sun'}
          {status === 'fast' && 'Too fast → Escaping into Space'}
        </div>
      </div>
    </div>
  );
};
